/** @format */

import React, { FC, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useLocation, matchPath } from "react-router-dom";
import { useFormik } from "formik";
import { useSchemaValidation } from "senf-shared";
import { AuthProps } from "./Auth.types";

import Wave from "../../atoms/shapes/Wave";
import SenfManSquatting from "../../../assets/illustrations/senfManSquatting.png";

import Box from "../../atoms/box/Box";
import corner from "../../../assets/shapes/corner.png";

import AuthOptions from "../../templates/auth/AuthOptions";
import AuthEmail from "../../templates/auth/AuthEmail";
import AuthResetPassword from "../../templates/auth/AuthResetPassword";
import AuthVerifyEmail from "../../templates/auth/AuthVerifyEmail";
import AuthAddDetails from "../../templates/auth/AuthAddDetails";

import { useModals } from "../../molecules/modalStack/ModalProvider";
import theme from "../../../styles/theme";
import Button from "../../core/button/Button";
import { cn } from "../../../util/classNameConstructor";

const Wrapper = styled.div<AuthProps>`
  position: relative;
  width: 100%;
  /* min-height: 700px; */
  overflow: hidden;
  top: 0;

  @media (max-width: 768px) {
    min-height: 100vh;
  }
`;
const Img = styled.img`
  position: absolute;

  margin-top: 41px;
  width: 126px;
  z-index: 1;
  pointer-events: none;
  user-select: none;
`;
const StyledSvg = styled.svg`
  position: absolute;
  /* right: -67px; */
  margin-top: 143px;
  z-index: 0;
  pointer-events: none;
  user-select: none;
`;

const Auth: FC<AuthProps> = ({
  authHandler,
  user,
  errorMessage,
  page,
  setPage,
  reAuth,
  loadUserDetails,
  project,
  heading,
  ...props
}) => {
  const { openModal } = useModals();
  const { t } = useTranslation();
  const validate = useSchemaValidation();

  const { pathname } = useLocation();

  const path = matchPath(
    {
      path: "/projects/:projectId",
      end: false,
    },
    pathname
  );
  const projectId = path?.params?.projectId || "";

  const validationSchema =
    page === "authEmail"
      ? validate.login
      : page === "authEmailRegister"
        ? validate.register
        : validate.email;
  const formikStore = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      handle: "",
      referrer: localStorage.getItem("referrer") || "",
      utm_id: localStorage.getItem("utm_id") || "",
      projectId: localStorage.getItem("projectId") || projectId,
      visitorId: localStorage.getItem("visitorId") || null,
      declarationOfConsent:
        localStorage.getItem("declarationOfConsent") === "true",
    },
    // enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: () => console.log("values"),
  });
  const [isLoggedIn] = React.useState(user);

  useEffect(() => {
    if (page === "authResetPassword") {
      openModal(<AuthResetPassword formikStore={formikStore} />, {
        id: "authResetPassword",
        title: `${t("auth:resetPassword")} `,
        description: t("auth:resetPasswordDescription"),
        afterClose: () => setPage("authEmail"),
      });
    }
  }, [page]);

  return (
    <Wrapper className="bg-surface-highlight-subdued">
      <div className="absolute m-3">
        {(page === "authEmail" || page === "authEmailRegister") && (
          <Button
            variant="shapeless"
            leadingIcon="arrowhead-left-md"
            onClick={() => {
              setPage("authOptions");
              // navigate("/");
            }}
          />
        )}
      </div>

      {page !== "authAddDetails" && (
        <Wave
          color={theme.colors.palette.primary}
          top="170px"
        />
      )}
      {page === "authAddDetails" && (
        <Img
          src={corner}
          alt="Illustration"
          style={{
            right: "0",
            top: "0",
            width: "90px",
            height: "90px",
            marginTop: "0",
          }}
        />
      )}
      {page !== "authAddDetails" && !reAuth && (
        <React.Fragment>
          <Img
            src={SenfManSquatting}
            alt="Illustration"
            className={cn(
              document.dir === "rtl" ? "left-6 scale-x-[-1]" : "right-6"
            )}
          />
          <StyledSvg
            xmlns="http://www.w3.org/2000/svg"
            width="175"
            height="69"
            className={cn(
              document.dir === "rtl"
                ? "-left-[64px] scale-x-[-1]"
                : "-right-[64px]"
            )}
          >
            <g>
              <defs>
                <linearGradient
                  id="idX0hbBYeIVg-1861781537"
                  gradientTransform="rotate(154, 0.5, 0.5)"
                >
                  <stop
                    offset="0"
                    stopColor="rgba(186, 163, 79, 0)"
                    stopOpacity="0"
                  ></stop>
                  <stop
                    offset="1"
                    stopColor="rgba(119, 108, 70, 0.46)"
                    stopOpacity="0.46"
                  ></stop>
                </linearGradient>
              </defs>
              <path
                d="M 0.5 68.5 L 174.5 0.5 L 174.5 33.5 L 56.5 67 L 62 55.5 L 26 67 Z"
                fill="url(#idX0hbBYeIVg-1861781537)"
                stroke="hsla(0, 0%, 100%, 0)"
              ></path>
            </g>
          </StyledSvg>
        </React.Fragment>
      )}

      {(() => {
        if (
          page === "authEmail" ||
          page === "authEmailRegister" ||
          page === "authResetPassword"
        ) {
          return (
            <AuthEmail
              setPage={setPage}
              authHandler={authHandler}
              errorMessage={errorMessage}
              formikStore={formikStore}
              variant={page === "authEmailRegister" ? "register" : "login"}
              validationSchema={validationSchema}
              project={project}
            />
          );
        }
        if (page === "authVerifyEmail") {
          return <AuthVerifyEmail formikStore={formikStore} />;
        }
        if (page === "authAddDetails") {
          return (
            <AuthAddDetails
              user={user}
              authHandler={authHandler}
              loadUserDetails={loadUserDetails}
              project={project}
            />
          );
        }
        return (
          <AuthOptions
            authHandler={authHandler}
            formikStore={formikStore}
            setPage={setPage}
            errorMessage={errorMessage}
            reAuth={reAuth}
            project={project}
            heading={heading}
            {...props}
          />
        );
      })()}
    </Wrapper>
  );
};

export default Auth;
